import {
  BreakPointCondition,
  UI,
  useBreakpointIf,
  HStack,
  Stack,
  VStack,
} from '@genial/components';
import { BoxBorder, Color, ColorTypes, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Pressable } from 'native-base';
import React from 'react';

import { GenialLimitTypes } from '../..';
import { EmptyList } from '../../../../../FinancialDetails/components/EmptyList';
import { ErrorListing } from '../../../../../FinancialDetails/components/ErrorListing';

import {
  availableBalanceNormalizedExplanation,
  genialLimitExplanation,
  genialLimitNoticeTexts,
  totalAllocatedExplanation,
} from './constants';

export const ItemDesktopWrapper: React.FC = () => (
  <Box w="full" h="1px" bgColor={Color['neutral.lightest']} />
);

const TextSkeleton: React.FC = () => (
  <UI.Text loadingHeight={Typography['fontsize.md']} maxW="55px" w="55px" isLoading />
);

export const GenialLimitContent: React.FC<GenialLimitTypes.Props> = ({
  error,
  onRefresh,
  isLoading,
  emptyGenialLimit,
  accountBalanceNormalized,
  availableBalanceNormalized,
  availableToAllocateNormalized,
  cardAllocatedNormalized,
  genialLimitNormalized,
  totalAllocatedNormalized,
  traderAllocatedNormalized,
}) => {
  const isTabletOrLower = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');
  const [noticeVisible, setNoticeVisible] = React.useState(true);

  const renderTooltip = (textExplanation: string, title: string, wordsPerLine = 0) => {
    if (isTabletOrLower) {
      return (
        <UI.Tooltip
          label={textExplanation}
          openAsAccordionExpanded
          mobileTitle={title}
          testID="info-tooltip">
          <UI.Icon name="Info" size="xxxs" color={Color['brand.tertiary.principal']} />
        </UI.Tooltip>
      );
    }

    return (
      <UI.Tooltip
        label={
          wordsPerLine ? Utils.Lines.breakIntoLines(textExplanation, wordsPerLine) : textExplanation
        }
        testID="info-tooltip">
        <Pressable>
          <UI.Icon name="Info" size="xxxs" color={Color['brand.tertiary.principal']} />
        </Pressable>
      </UI.Tooltip>
    );
  };

  const renderInfo = (
    label: string,
    value: string,
    color: ColorTypes.Option = 'neutral.darkest',
    textExplanation?: string,
    wordsPerLine = 0
  ) => {
    return (
      <HStack justifyContent="space-between">
        <HStack space={Spacing['inline.nano']}>
          <UI.Text color={color}>{label}</UI.Text>
          {!!textExplanation && renderTooltip(textExplanation, label, wordsPerLine)}
        </HStack>
        {isLoading ? <TextSkeleton /> : <UI.Text color={color}>{value}</UI.Text>}
      </HStack>
    );
  };

  if (error) {
    return (
      <UI.Shadow
        level={1}
        borderRadius={BoxBorder.heavy}
        my={Spacing['inset.sm']}
        paddingX={Spacing['inline.xxxs']}
        paddingY={Spacing['inline.xxs']}>
        <ErrorListing handleReload={onRefresh} />
      </UI.Shadow>
    );
  }

  if (emptyGenialLimit) {
    return (
      <UI.Shadow
        level={1}
        borderRadius={BoxBorder.heavy}
        my={Spacing['inset.sm']}
        paddingX={Spacing['inline.xxxs']}
        paddingY={Spacing['inline.xxs']}>
        <EmptyList
          title="Cliente não possui limite genial."
          description="No momento este cliente nao possui limite genial."
        />
      </UI.Shadow>
    );
  }

  return (
    <UI.Shadow
      level={1}
      borderRadius={BoxBorder.heavy}
      my={Spacing['inset.sm']}
      paddingX={Spacing['inline.xxxs']}
      paddingY={Spacing['inline.xxs']}>
      <VStack space={Spacing['stack.xxs']}>
        <Stack
          justifyContent="space-between"
          space={Spacing['stack.nano']}
          alignItems={Utils.Responsive.DesktopPhone({ desktop: 'center', phone: 'inherit' })}
          direction={Utils.Responsive.DesktopPhone({ desktop: 'row', phone: 'column' })}>
          <Stack
            space={Spacing['stack.quarck']}
            justifyContent="space-between"
            direction={Utils.Responsive.DesktopPhone({ desktop: 'column', phone: 'row-reverse' })}>
            {isLoading ? (
              <TextSkeleton />
            ) : (
              <UI.Text variant="semibold" fontSize={Typography['fontsize.xl']}>
                {genialLimitNormalized}
              </UI.Text>
            )}

            <HStack space={Spacing['inline.nano']}>
              <UI.Text variant="semibold" color="neutral.medium" textTransform="uppercase">
                Limite genial
              </UI.Text>
              {renderTooltip(genialLimitExplanation, 'Limite genial', 12)}
            </HStack>
          </Stack>
          <Stack
            space={Spacing['stack.quarck']}
            alignItems="end"
            justifyContent="space-between"
            direction={Utils.Responsive.DesktopPhone({ desktop: 'column', phone: 'row-reverse' })}>
            {isLoading ? (
              <TextSkeleton />
            ) : (
              <UI.Text variant="semibold">{totalAllocatedNormalized}</UI.Text>
            )}

            <HStack space={Spacing['inline.nano']}>
              <UI.Text variant="semibold" color="neutral.medium" textTransform="uppercase">
                Total alocado
              </UI.Text>
              {renderTooltip(totalAllocatedExplanation, 'Total alocado', 8)}
            </HStack>
          </Stack>
        </Stack>
        <Box width="full" borderTopWidth={1} borderColor={Color['neutral.lightest']} />
        {!!noticeVisible && (
          <UI.Notice
            testId="genial-limit-notice"
            type="warning"
            backgroundColor={Utils.ColorToken.WithOpacity(
              'feedback.attention.lightest',
              'opacity.medium'
            )}
            textObjects={genialLimitNoticeTexts}
            onPress={() => setNoticeVisible(false)}
          />
        )}
        <VStack space={Spacing['stack.xxxs']}>
          {renderInfo('Alocado no cartão', cardAllocatedNormalized)}
          {renderInfo('Alocado em Trader Cockpit', traderAllocatedNormalized)}
          {renderInfo('Disponível para alocação', availableToAllocateNormalized, 'neutral.medium')}
        </VStack>
        <Box width="full" borderTopWidth={1} borderColor={Color['neutral.lightest']} />
        <VStack space={Spacing['stack.xxxs']}>
          {renderInfo('Saldo em conta', accountBalanceNormalized)}
          {renderInfo(
            'Saldo Disponível para transação',
            availableBalanceNormalized,
            'neutral.darkest',
            availableBalanceNormalizedExplanation,
            14
          )}
        </VStack>
      </VStack>
    </UI.Shadow>
  );
};
